import { useMemo } from 'react';
import useSearchParams from '@elementary/common-ui/hooks/useSearchParams';
import useGraphqlPagedQuery from '@elementary/common-ui/hooks/useGraphqlPagedQuery';
import useGraphqlQuery from '@elementary/common-ui/hooks/useGraphqlQuery';
import useUrlFilters from '@elementary/common-ui/hooks/useUrlFilters';
import useSelectedTreeItem from '@elementary/common-ui/hooks/useSelectedTreeItem';
import useTreeViewParams from '@elementary/common-ui/hooks/useTreeViewParams';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import { hashArray } from '@elementary/common-ui/utils/hashing';
import {
  MODEL_RUNS_TABLE_ASSETS,
  MODEL_RUNS_TABLE_ASSETS_FILTER_OPTIONS,
} from '@elementary/common-ui/constants/queryKeys';
import { DEFAULT_DAYS_BACK, PAGE_SIZE } from '@elementary/common-ui/constants';
import {
  MODEL_RUNS_TABLE_FILTERS,
  MODEL_RUNS_TABLE_SORT,
} from '@elementary/common-ui/constants/urlParams';
import { SortDirection } from '@elementary/common-ui/components/Table/types';
import { ModelRuns } from '@elementary/common-ui/types';
import { parseGraphqlFilterOptions } from '@elementary/common-ui/utils/graphqlFilters';
import {
  getModelRunsAssetsFilterOptionsQuery,
  getModelRunsTableAssetsQuery,
} from '../api';
import { COLUMN_TO_FIELD_MAP, FIELD_TO_COLUMN_MAP } from '../consts';
import {
  ModelRunsAssetsFilterOptionsResponse,
  ModelRunsTableAssetsPagedResponse,
} from '../types';
import {
  applyModelRunsClientSideFilters,
  mapUrlFiltersToTableAssetFilters,
  parseTableAssetToModelRuns,
} from '../utils';

type UrlSort = { id: string; direction: SortDirection };

const DEFAULT_SORT: UrlSort = {
  id: 'last_exec_time',
  direction: 'desc',
};

const DIRECTION_TO_GQL: Record<SortDirection, 'ASC' | 'DESC'> = {
  asc: 'ASC',
  desc: 'DESC',
};

type Props = {
  search?: string;
  enabled?: boolean;
};

const useModelRunsTableDataGraphql = ({ search, enabled = true }: Props) => {
  const selectedEnvId = useSelectedEnv();
  const { selectedTreeFolderNodes, selectedTreeNode } = useSelectedTreeItem();
  const { urlTreeNode, urlSavedView } = useTreeViewParams();
  const [searchParams] = useSearchParams();

  const { urlFiltersDebounced } = useUrlFilters({
    urlKey: MODEL_RUNS_TABLE_FILTERS,
  });

  const selectedTreeNodeIds = useMemo(() => {
    if (!urlTreeNode && !urlSavedView) {
      return undefined;
    }

    if (selectedTreeFolderNodes?.length) {
      return selectedTreeFolderNodes.map((node) => node.id);
    }

    if (selectedTreeNode) {
      return [selectedTreeNode.id];
    }

    return [];
  }, [selectedTreeFolderNodes, selectedTreeNode, urlSavedView, urlTreeNode]);

  const selectedTreeNodeIdsHash = useMemo(
    () => (selectedTreeNodeIds ? hashArray(selectedTreeNodeIds) : null),
    [selectedTreeNodeIds],
  );

  const urlSort: UrlSort = useMemo(
    () =>
      JSON.parse(searchParams.get(MODEL_RUNS_TABLE_SORT) || 'null') ||
      DEFAULT_SORT,
    [searchParams],
  );

  const { tableAssetFilters, clientSideFilters } = useMemo(
    () => mapUrlFiltersToTableAssetFilters(urlFiltersDebounced),
    [urlFiltersDebounced],
  );

  const sort = useMemo(() => {
    const field = COLUMN_TO_FIELD_MAP[urlSort.id];
    if (!field) return undefined;
    return {
      field,
      direction: DIRECTION_TO_GQL[urlSort.direction],
    };
  }, [urlSort]);

  const hasEmptyTreeSelection = selectedTreeNodeIds?.length === 0;

  const filters = useMemo(
    () => ({
      ...tableAssetFilters,
      tableTypes: ['MODEL'],
      ids: selectedTreeNodeIds?.length ? selectedTreeNodeIds : null,
      search: search?.trim() ? search.trim() : null,
    }),
    [tableAssetFilters, selectedTreeNodeIds, search],
  );

  const { data, isFetching, isLoading, isError, fetchNextPage, hasNextPage } =
    useGraphqlPagedQuery<ModelRuns, ModelRunsTableAssetsPagedResponse>({
      queryKey: [
        MODEL_RUNS_TABLE_ASSETS,
        selectedEnvId,
        selectedTreeNodeIdsHash,
        urlFiltersDebounced,
        urlSort,
        search,
      ],
      query: getModelRunsTableAssetsQuery,
      variables: {
        filters,
        sort,
        limit: PAGE_SIZE,
        daysBack: DEFAULT_DAYS_BACK,
      },
      select: (response) => ({
        items: response.tableAssets.items.map(parseTableAssetToModelRuns),
        pageInfo: response.tableAssets.pageInfo,
      }),
      enabled: enabled && !hasEmptyTreeSelection,
      envId: selectedEnvId,
    });

  const tableData = useMemo(() => {
    if (hasEmptyTreeSelection) {
      return [];
    }

    return applyModelRunsClientSideFilters(data, clientSideFilters);
  }, [clientSideFilters, data, hasEmptyTreeSelection]);

  const { data: filterOptionsData, isFetching: isFilterOptionsFetching } =
    useGraphqlQuery<ModelRunsAssetsFilterOptionsResponse>({
      query: getModelRunsAssetsFilterOptionsQuery,
      queryKey: [
        MODEL_RUNS_TABLE_ASSETS_FILTER_OPTIONS,
        selectedEnvId,
        selectedTreeNodeIdsHash,
      ],
      variables: {
        filters: {
          ids: selectedTreeNodeIds?.length ? selectedTreeNodeIds : null,
          tableTypes: ['MODEL'],
        },
      },
      enabled: enabled && !hasEmptyTreeSelection,
      envId: selectedEnvId,
    });

  const filterOptions = useMemo(
    () =>
      parseGraphqlFilterOptions(
        filterOptionsData?.tableAssetsFilterOptions || [],
        FIELD_TO_COLUMN_MAP,
      ),
    [filterOptionsData],
  );

  return {
    tableData,
    isTableAssetsFetching: isLoading || isFetching,
    isError,
    fetchNextPage,
    hasNextPage,
    filterOptions,
    isFilterOptionsFetching,
  };
};

export default useModelRunsTableDataGraphql;
